import { configuration } from '../configuration'
import { helpers, type PluginConfiguration } from '../helpers'
import { icons } from '../icons'

const consoleFunctionsToOverwrite = ['log', 'info', 'error', 'warn'] as const

const overwriteConsole = () => {
  const originalConsoleFunctions = Object.fromEntries(
    // biome-ignore lint/suspicious/noConsole: <please specify a reason here>
    consoleFunctionsToOverwrite.map(func => [func, console[func].bind(console)])
  )

  consoleFunctionsToOverwrite.forEach(func => {
    console[func] = (...args: unknown[]) => {
      originalConsoleFunctions[func]?.(...args)
      pluginConsole.layout.render({ type: func, data: args })
    }
  })
}

const colors: Record<(typeof consoleFunctionsToOverwrite)[number], string> = {
  log: 'bg-[#b9f8cf]',
  info: 'bg-[#fff085]',
  error: 'bg-[#ffc9c9]',
  warn: 'bg-[#ffd7a8]',
}

export const pluginConsole: PluginConfiguration = {
  name: 'Console',
  id: 'console',
  icon: icons.console,
  extra: '',
  layout: {
    type: 'table',
    columns: [['Message']],
    searchColumnsIndex: [0],
    // @ts-expect-error Fix TypeScript type
    render: (data: { type: (typeof consoleFunctionsToOverwrite)[number]; data: unknown[] }) => {
      if (data.type === 'error') {
        helpers.incrementNotificationBadge(pluginConsole.id)
      }

      helpers.updateTable(pluginConsole.id, 'append', {
        rows: [
          {
            dataAttributes: {
              'data-type': data.type,
              'data-data': data.data,
            },
            data: [
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'px-4 py-2 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: data.data.map(part => String(part)).join(' '),
                    content: () => {
                      const createdElements = helpers.createHTMLElements([
                        {
                          className: `inline-block w-4 h-4 text-[#1e293b] rounded-full mr-2 uppercase text-xs/[17px] text-center ${colors[data.type]}`,
                          title: data.type.toUpperCase(),
                          content: data.type[0],
                          element: 'span',
                        },
                        {
                          className: '',
                          element: 'span',
                          content: () =>
                            helpers.createHTMLElements(
                              data.data.map(part => {
                                if (typeof part === 'object') {
                                  return {
                                    className: 'text-[#22d3ee] mr-1',
                                    content: helpers.convertObjectToString(
                                      part as Record<string, unknown>
                                    ),
                                    element: 'span',
                                  }
                                }
                                return {
                                  className: 'mr-1',
                                  content: String(part),
                                  element: 'span',
                                }
                              })
                            ),
                        },
                      ])

                      return createdElements
                    },
                  }).outerHTML,
              },
            ],
          },
        ],
        isRowClickable: true,
      })
    },
  },
  render: (...data: unknown[]) => pluginConsole.layout.render(data),

  init: () => {
    if (configuration.plugins.find(plugin => plugin.id === pluginConsole.id)) {
      overwriteConsole()
    }

    document
      .querySelector(`[data-fu-tools-table-content=${pluginConsole.id}]`)
      ?.addEventListener('click', event => {
        const tableRow = (event.target as HTMLElement).closest('[fu-tools-table-row]')
        if (!tableRow) {
          return
        }

        const type = (tableRow.getAttribute('data-type') ?? '') as keyof typeof colors
        const rawData = tableRow.getAttribute('data-data')
        const data: string[] = Array.isArray(rawData) ? rawData : JSON.parse(rawData ?? '[]')

        const documentFragment = helpers.createHTMLElements([
          { className: 'flex font-bold border-[#334155] border-b pb-4', content: type },
          {
            className:
              'border-[#334155] border-b py-4 cursor-pointer hover:bg-[#1e293b] transition-all duration-200',
            content: () =>
              helpers.createHTMLElements(
                data.map(part => {
                  if (typeof part === 'object') {
                    return {
                      className: 'text-[#22d3ee] mr-1',
                      content: helpers.convertObjectToString(part as Record<string, unknown>),
                      element: 'span',
                    }
                  }
                  return {
                    className: 'mr-1',
                    content: String(part),
                    element: 'span',
                  }
                })
              ),
            copyOnClick: true,
          },
        ])
        helpers.showModal(documentFragment)
      })
  },
}
